import type { MedicalResult } from '../types/result.types';

/**
 * Result categories — a 1:1 port of the mobile `ConsultationType` enum. Each
 * MEDIS `ConsType` is matched (case-insensitive) to one of these fixed buckets;
 * anything unrecognized falls into "Diverse".
 */
export type ResultCategoryKey =
  | 'ambulator'
  | 'laborator'
  | 'radiologie'
  | 'anatomie'
  | 'operatiuni'
  | 'bord'
  | 'diverse';

export interface ResultCategory {
  key: ResultCategoryKey;
  /** Backend `ConsType` code matched against (lowercased). */
  code: string;
  name: string;
  description: string;
}

/** Display order matches the mobile enum declaration order. */
export const RESULT_CATEGORIES: ResultCategory[] = [
  { key: 'ambulator', code: 'Ambulator', name: 'Ambulator', description: 'Servicii ambulatorii' },
  { key: 'laborator', code: 'Laborator', name: 'Laborator', description: 'Analize medicale' },
  { key: 'radiologie', code: 'Radiologie', name: 'Radiologie', description: 'Investigații imagistice' },
  { key: 'anatomie', code: 'Anatomie patologica', name: 'Anatomie patologică', description: 'Examinări histopatologice' },
  { key: 'operatiuni', code: 'Operatiuni diverse', name: 'Operațiuni diverse', description: 'Intervenții chirurgicale' },
  { key: 'bord', code: 'Bord multidisciplinar', name: 'Bord multidisciplinar', description: 'Ședințe multidisciplinare' },
  { key: 'diverse', code: 'Diverse', name: 'Diverse', description: 'Alte tipuri de consultații' },
];

const BY_CODE = new Map(RESULT_CATEGORIES.map((c) => [c.code.toLowerCase(), c]));
const DIVERSE = RESULT_CATEGORIES[RESULT_CATEGORIES.length - 1]!;

/** Resolve a `ConsType` to its category (defaults to "Diverse"). */
export function categoryForConsType(consType: string | null | undefined): ResultCategory {
  if (!consType) return DIVERSE;
  return BY_CODE.get(consType.toLowerCase()) ?? DIVERSE;
}

/** Categories for which an "in progress" status must not be surfaced to patients. */
const HIDE_IN_PROGRESS: ReadonlySet<ResultCategoryKey> = new Set(['ambulator', 'radiologie']);

/**
 * Whether an unfinished result should show its "in progress" badge. Ambulator and
 * radiologie results never surface that state; other categories always do.
 */
export function showsInProgress(consType: string | null | undefined): boolean {
  return !HIDE_IN_PROGRESS.has(categoryForConsType(consType).key);
}

export interface GroupedResults {
  category: ResultCategory;
  results: MedicalResult[];
}

/** Group results into categories, preserving the fixed category order, non-empty only. */
export function groupResultsByCategory(results: MedicalResult[]): GroupedResults[] {
  const buckets = new Map<ResultCategoryKey, MedicalResult[]>();
  for (const result of results) {
    const cat = categoryForConsType(result.consType);
    const list = buckets.get(cat.key);
    if (list) list.push(result);
    else buckets.set(cat.key, [result]);
  }
  return RESULT_CATEGORIES.filter((c) => buckets.has(c.key)).map((category) => ({
    category,
    results: buckets.get(category.key)!,
  }));
}
